import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shows } from '@/mocks/shows';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '电视剧', to: '/category/tv' },
  { label: '电影', to: '/category/movie' },
  { label: '资讯', to: '/news' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const suggestions = keyword.trim()
    ? shows.filter((s) => s.title.toLowerCase().includes(keyword.trim().toLowerCase())).slice(0, 6)
    : [];

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return;
    navigate(`/category/tv/search-${encodeURIComponent(keyword.trim())}`);
    setShowSuggestions(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 w-full max-w-[1440px] items-center justify-between px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-film-fill text-lg"></i>
          </span>
          <span
            className={`font-heading text-lg md:text-xl font-black tracking-wide ${
              scrolled ? 'text-foreground-950' : 'text-foreground-950'
            }`}
          >
            同学的妈妈
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="font-label text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center relative">
          <form onSubmit={handleSearchSubmit} className="relative">
            <div className="flex items-center bg-background-100 border border-background-300 rounded-full px-4 py-2 w-64 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-500 text-sm mr-2"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder="搜索剧名、影片名称"
                className="bg-transparent text-sm outline-none w-full text-foreground-900 placeholder:text-foreground-400 font-body"
              />
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-background-50 border border-background-200 rounded-lg shadow-none overflow-hidden z-50">
                {suggestions.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-background-100 cursor-pointer"
                  >
                    <div className="w-8 h-10 rounded overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={item.cover} alt={item.title} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-900 truncate">{item.title}</p>
                      <p className="text-xs text-foreground-500">{item.year} · {item.type === 'tv' ? '电视剧' : '电影'}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </form>
        </div>

        <button
          className="md:hidden flex items-center justify-center w-10 h-10 text-foreground-900 cursor-pointer"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="菜单"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 space-y-4">
          <form onSubmit={handleSearchSubmit} className="flex items-center bg-background-100 border border-background-300 rounded-full px-4 py-2">
            <i className="ri-search-line text-foreground-500 text-sm mr-2"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索剧名、影片名称"
              className="bg-transparent text-sm outline-none w-full text-foreground-900 placeholder:text-foreground-400 font-body"
            />
          </form>
          <nav className="flex flex-col gap-1">
            {navLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="font-label text-sm font-medium text-foreground-800 py-2.5 border-b border-background-200 last:border-0 cursor-pointer whitespace-nowrap"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}